// เหตุการณ์ตามประเภทพื้นที่ (biome) ตรงที่เราอยู่ (docs/PLAN.md ข้อ 4.2–4.3)
// เกิดเฉพาะเมื่อมีตำแหน่งบนแผนที่ ประเภทพื้นที่มาจาก RESOLVE Ecoregions 2017 และความลึกทะเล

import type { ArchetypeId } from "../../data/groups";
import type { GameEvent } from "../../engine/types";

// สัตว์ที่ใช้ชีวิตบนบก (ปลาน้ำจืดอยู่ในช่องบนบกก็จริง แต่ไม่เจอไฟป่าหรือพายุทราย)
const LAND: readonly ArchetypeId[] = ["MAMMAL", "BIRD", "REPTILE", "AMPHIBIAN", "INSECT", "SOCIAL_INSECT", "ARACHNID", "MYRIAPOD", "MOLLUSC", "WORM"];
const SEA: readonly ArchetypeId[] = ["FISH", "MAMMAL", "REPTILE", "BIRD", "CRUSTACEAN", "MOLLUSC", "ECHINODERM", "WORM", "SESSILE"];
const ACTIVE = ["young", "adult", "old"] as const;

export const BIOME_EVENTS: readonly GameEvent[] = [
  // ---------- ป่าดิบชื้นเขตร้อน ----------
  {
    id: "rainforest_fig",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["tropical_moist_forest"], sessile: false },
    weight: 4,
    text: "ต้นไทรใหญ่กลางป่าออกผลสุกเต็มต้น สัตว์จากทั่วป่าพากันมาที่นี่",
    fact: "ต้นไทรออกผลได้หลายครั้งในหนึ่งปีและไม่พร้อมกันทั้งป่า จึงเป็นแหล่งอาหารสำคัญของสัตว์ในป่าชื้นเขตร้อนช่วงที่ผลไม้อื่นขาดแคลน",
    choices: [
      {
        label: "ไปหาอาหารที่ต้นไทร",
        outcomes: [
          { chance: 0.75, text: "รอบต้นไทรอุดมไปด้วยอาหาร ทั้งผลไม้และสัตว์ที่มากินผลไม้", effects: { fullness: 35 } },
          { chance: 0.25, text: "นักล่าก็รู้ว่าสัตว์มารวมกันที่นี่ คุณต้องหนีออกมากลางคัน", effects: { fullness: 10, safety: -15 } },
        ],
      },
      { label: "หาอาหารที่อื่นที่เงียบกว่า", outcomes: [{ chance: 1, text: "คุณได้อาหารน้อยกว่า แต่ไม่ต้องเบียดกับใคร", effects: { fullness: 15, safety: 5 } }] },
    ],
  },
  {
    id: "rainforest_downpour",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["tropical_moist_forest", "tropical_conifer_forest"] },
    weight: 3,
    text: "ฝนตกหนักทุกบ่าย ลำธารในป่าเริ่มล้นตลิ่ง",
    fact: "ป่าดิบชื้นเขตร้อนหลายแห่งมีฝนตกมากกว่า 2,000 มิลลิเมตรต่อปี",
    choices: [
      { label: "หลบฝนใต้ใบไม้ใหญ่", outcomes: [{ chance: 1, text: "ใบไม้ใหญ่กันฝนได้ดี คุณรอจนฝนซา", effects: { safety: 5, fullness: -5 } }] },
      {
        label: "ออกหากินกลางสายฝน",
        outcomes: [
          { chance: 0.6, text: "เสียงฝนกลบเสียงฝีเท้าของคุณ เหยื่อไม่รู้ตัวเลย", effects: { fullness: 25 } },
          { chance: 0.4, text: "น้ำป่าไหลหลากมากะทันหัน คุณถูกพัดไปไกล", effects: { health: -20, move: "near", cause: "disaster" } },
        ],
      },
    ],
  },

  // ---------- ป่าผลัดใบเขตร้อน ----------
  {
    id: "dry_forest_fire",
    archetypes: LAND,
    stageTags: ["dependent", ...ACTIVE],
    requires: { biome: ["tropical_dry_forest", "tropical_savanna"] },
    weight: 3,
    threatenedWeight: 1.5,
    text: "หน้าแล้ง ใบไม้แห้งร่วงหนาเต็มพื้น แล้วควันไฟก็ลอยมาตามลม",
    fact: "ป่าผลัดใบอย่างป่าเต็งรังเกิดไฟป่าแทบทุกปีในหน้าแล้ง ต้นไม้หลายชนิดจึงมีเปลือกหนาทนไฟ",
    choices: [
      {
        label: "หนีไปทางเหนือลม",
        outcomes: [
          { chance: 0.75, text: "คุณหนีพ้นแนวไฟ ไปอยู่ในป่าที่ยังไม่ไหม้", effects: { fullness: -10, move: "near" } },
          { chance: 0.25, text: "ควันหนาจนหายใจไม่ออก คุณรอดมาได้แต่อ่อนแรง", effects: { health: -25, move: "near", cause: "disaster" } },
        ],
      },
      {
        label: "หลบลงโพรงหรือซอกหิน",
        outcomes: [
          { chance: 0.6, text: "ไฟลามผ่านไปข้างบน ในโพรงยังเย็นอยู่", effects: { safety: 5 } },
          { chance: 0.3, text: "ความร้อนแผ่ลงมาถึงที่ซ่อน", effects: { health: -30, cause: "disaster" } },
          { chance: 0.1, text: "ไฟลุกโหมรุนแรงเกินกว่าจะรอดได้", effects: { death: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "dry_forest_leafless",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["tropical_dry_forest"], sessile: false },
    weight: 3,
    text: "ต้นไม้ทิ้งใบจนป่าโปร่งโล่ง มองเห็นไปได้ไกล",
    fact: "ต้นไม้ในป่าผลัดใบทิ้งใบในหน้าแล้งเพื่อลดการเสียน้ำ แล้วผลิใบใหม่พร้อมฝนแรก",
    choices: [
      { label: "ซ่อนตัวในจุดที่ยังมีพุ่มไม้", outcomes: [{ chance: 1, text: "คุณใช้เวลาส่วนใหญ่ซ่อนอยู่ในพุ่มไม้ที่เหลือ", effects: { safety: 10, fullness: -5 } }] },
      {
        label: "ออกหาอาหารตามปกติ",
        outcomes: [
          { chance: 0.6, text: "ป่าโปร่งทำให้คุณเห็นอาหารได้ไกลเหมือนกัน", effects: { fullness: 25 } },
          { chance: 0.4, text: "นักล่าก็เห็นคุณได้ไกลเช่นกัน", effects: { fullness: 10, safety: -20 } },
        ],
      },
    ],
  },

  // ---------- ทุ่งหญ้าสะวันนา ----------
  {
    id: "savanna_waterhole",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["tropical_savanna"], sessile: false },
    weight: 4,
    text: "กลางหน้าแล้ง แหล่งน้ำเหลืออยู่แค่แอ่งเดียว สัตว์ทุกชนิดต้องมาที่นี่",
    fact: "ทุ่งหญ้าสะวันนามีฤดูฝนและฤดูแล้งชัดเจน สัตว์กินพืชหลายชนิดอพยพตามฝนเพื่อหาหญ้าใหม่",
    choices: [
      {
        label: "รีบลงไปกินน้ำ",
        outcomes: [
          { chance: 0.7, text: "คุณกินน้ำจนชุ่มคอแล้วรีบถอยออกมา", effects: { health: 10 } },
          { chance: 0.3, text: "นักล่าซุ่มอยู่ริมแอ่งน้ำ คุณหนีมาได้อย่างหวุดหวิด", effects: { health: -20, safety: -10, cause: "predator" } },
        ],
      },
      { label: "เดินทางไปหาแหล่งน้ำอื่น", outcomes: [{ chance: 1, text: "การเดินทางยาวไกลและร้อน แต่ปลอดภัยกว่า", effects: { fullness: -15, health: 5, move: "near" } }] },
    ],
  },
  {
    id: "savanna_fire_feast",
    archetypes: ["BIRD", "MAMMAL", "REPTILE"],
    stageTags: ["adult", "old"],
    requires: { biome: ["tropical_savanna", "temperate_grassland"], sessile: false },
    weight: 2,
    text: "ไฟลามทุ่งหญ้าแห้ง แมลงและสัตว์ตัวเล็กพากันหนีออกมาจากกองหญ้า",
    fact: "นกบางชนิดบินตามแนวไฟในทุ่งหญ้าเพื่อจับแมลงที่หนีไฟ และไฟยังทำให้หญ้าใหม่งอกขึ้นหลังฝนตก",
    choices: [
      {
        label: "ตามแนวไฟไปจับสัตว์ที่หนีออกมา",
        outcomes: [
          { chance: 0.8, text: "เหยื่อวิ่งออกมาหาคุณเองไม่หยุด", effects: { fullness: 35 } },
          { chance: 0.2, text: "ลมเปลี่ยนทิศ ไฟลามเข้ามาหาคุณ", effects: { health: -20, cause: "disaster" } },
        ],
      },
      { label: "อยู่ห่างจากไฟ", outcomes: [{ chance: 1, text: "คุณรอจนไฟดับแล้วค่อยกลับมา", effects: { safety: 5 } }] },
    ],
  },

  // ---------- ทุ่งหญ้าเขตอบอุ่น ----------
  {
    id: "grassland_wind",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["temperate_grassland", "montane_grassland"], sessile: false },
    weight: 3,
    text: "ลมแรงพัดผ่านทุ่งหญ้ากว้างที่แทบไม่มีต้นไม้ให้กำบัง",
    fact: "ทุ่งหญ้าเขตอบอุ่นอย่างสเตปป์และแพรรีมีต้นไม้น้อยมาก สัตว์หลายชนิดจึงขุดโพรงอยู่ใต้ดิน",
    choices: [
      { label: "หลบในโพรงหรือแอ่งดิน", outcomes: [{ chance: 1, text: "ใต้ดินอบอุ่นและเงียบ ลมพัดผ่านไปข้างบน", effects: { safety: 10, fullness: -5 } }] },
      {
        label: "ฝ่าลมออกไปหาอาหาร",
        outcomes: [
          { chance: 0.6, text: "ลมแรงกลบกลิ่นของคุณ คุณหาอาหารได้โดยไม่มีใครเห็น", effects: { fullness: 25 } },
          { chance: 0.4, text: "ลมหนาวทำให้คุณหนาวสั่นจนอ่อนแรง", effects: { health: -15, cause: "disaster" } },
        ],
      },
    ],
  },

  // ---------- ทุ่งหญ้าน้ำท่วมถึง ----------
  {
    id: "floodplain_rise",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["flooded_grassland"] },
    weight: 4,
    text: "ฤดูน้ำหลากมาถึง ทุ่งหญ้ากว้างกลายเป็นผืนน้ำสุดสายตา",
    fact: "ทุ่งหญ้าน้ำท่วมถึงอย่างแพนทานัลในอเมริกาใต้ถูกน้ำท่วมนานหลายเดือนทุกปี",
    choices: [
      {
        label: "ย้ายขึ้นไปอยู่บนเนินดิน",
        outcomes: [
          { chance: 0.8, text: "บนเนินเบียดกันแน่นด้วยสัตว์หลายชนิด แต่คุณปลอดภัย", effects: { safety: -5, move: "near" } },
          { chance: 0.2, text: "เนินดินที่คุณเลือกถูกน้ำท่วมตามมา", effects: { health: -20, move: "near", cause: "disaster" } },
        ],
      },
      { label: "ใช้ประโยชน์จากน้ำที่เพิ่มขึ้น", outcomes: [{ chance: 1, text: "น้ำพาปลา กบ และแมลงน้ำมาเต็มทุ่ง อาหารมีไม่ขาด", effects: { fullness: 30 } }] },
    ],
  },

  // ---------- ทุ่งหญ้าบนภูเขาสูง ----------
  {
    id: "highland_thin_air",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["montane_grassland", "rock_ice"] },
    weight: 3,
    text: "อากาศบนที่สูงเบาบาง กลางคืนหนาวจัดจนน้ำค้างกลายเป็นน้ำแข็ง",
    fact: "ยิ่งสูงอากาศยิ่งเบาบาง สัตว์บนที่สูงหลายชนิดมีเลือดที่จับออกซิเจนได้ดีกว่าญาติที่อยู่ที่ต่ำ",
    choices: [
      { label: "ลงไปหากินที่ต่ำลงมาหน่อย", outcomes: [{ chance: 1, text: "ข้างล่างอุ่นกว่าและมีอาหารมากกว่า", effects: { fullness: 15, health: 5, move: "near" } }] },
      { label: "อยู่ที่เดิม ผิงแดดตอนกลางวัน", outcomes: [{ chance: 1, text: "แดดแรงบนที่สูงช่วยให้ร่างกายอุ่นขึ้น", effects: { health: 5, fullness: -5 } }] },
    ],
  },

  // ---------- ป่าเขตอบอุ่นและเขตหนาว ----------
  {
    id: "autumn_mast",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["temperate_broadleaf_forest", "temperate_conifer_forest"], sessile: false },
    weight: 3,
    text: "ใบไม้เปลี่ยนเป็นสีแดงส้ม ลูกโอ๊กและลูกสนร่วงเต็มพื้นป่า",
    fact: "ต้นโอ๊กบางปีออกผลมากผิดปกติพร้อมกันทั้งป่า เรียกว่าปีมาสต์ (mast year) สัตว์ที่กินลูกโอ๊กจึงมีลูกมากขึ้นในปีถัดไป",
    choices: [
      { label: "กินให้อ้วนก่อนฤดูหนาว", outcomes: [{ chance: 1, text: "คุณสะสมไขมันไว้เต็มที่", effects: { fullness: 35, health: 5 } }] },
      {
        label: "ซ่อนอาหารไว้กินทีหลัง",
        outcomes: [
          { chance: 0.7, text: "คุณซ่อนเสบียงไว้หลายแห่ง พอให้ผ่านฤดูหนาว", effects: { fullness: 15, safety: 5 } },
          { chance: 0.3, text: "คุณลืมที่ซ่อนไปครึ่งหนึ่ง แต่เมล็ดเหล่านั้นจะงอกเป็นต้นไม้ใหม่", effects: { fullness: 10 } },
        ],
      },
    ],
  },
  {
    id: "winter_snow",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["temperate_broadleaf_forest", "temperate_conifer_forest", "boreal_forest", "temperate_grassland"] },
    weight: 3,
    text: "ฤดูหนาวมาถึง หิมะปกคลุมทุกอย่าง อาหารหายากขึ้นทุกวัน",
    fact: "สัตว์หลายชนิดในเขตอบอุ่นและเขตหนาวจำศีลหรือลดการเผาผลาญพลังงานลงในฤดูหนาว",
    choices: [
      { label: "หาที่หลบนิ่งๆ ประหยัดพลังงาน", outcomes: [{ chance: 1, text: "คุณผ่านช่วงที่หนาวที่สุดไปได้ แม้ร่างกายจะผอมลง", effects: { fullness: -10, safety: 10 } }] },
      {
        label: "ขุดหิมะหาอาหาร",
        outcomes: [
          { chance: 0.6, text: "ใต้หิมะยังมีอาหารเหลืออยู่", effects: { fullness: 20, health: -5 } },
          { chance: 0.4, text: "หิมะหนาเกินไป คุณหนาวจนอ่อนแรง", effects: { health: -20, fullness: -10, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "taiga_summer_insects",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["boreal_forest", "tundra"] },
    weight: 3,
    text: "ฤดูร้อนสั้นๆ มาถึง ยุงและริ้นออกมาเป็นฝูงหนาแน่นจนท้องฟ้ามืด",
    fact: "ป่าไทกาเป็นระบบนิเวศบนบกที่กว้างใหญ่ที่สุดในโลก ทอดยาวรอบซีกโลกเหนือ",
    choices: [
      {
        label: "กินแมลงให้เต็มที่",
        outcomes: [
          { chance: 0.6, text: "แมลงมีมากจนกินไม่หวาดไม่ไหว", effects: { fullness: 30 } },
          { chance: 0.4, text: "คุณไม่ได้กินแมลง แต่แมลงกินคุณ ยุงรุมกัดทั้งวัน", effects: { health: -10, fullness: 5 } },
        ],
      },
      { label: "ไปอยู่ที่ลมแรงให้แมลงน้อยลง", outcomes: [{ chance: 1, text: "ลมพัดฝูงยุงออกไป คุณได้พักบ้าง", effects: { health: 5 } }] },
    ],
  },

  // ---------- ทุนดราและธารน้ำแข็ง ----------
  {
    id: "tundra_midnight_sun",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["tundra"] },
    weight: 3,
    text: "ดวงอาทิตย์ไม่ตกดินเลยทั้งคืน ทุ่งทุนดราผลิดอกเล็กๆ เต็มไปหมด",
    fact: "ในฤดูร้อนเหนือวงกลมอาร์กติก ดวงอาทิตย์ไม่ตกดินติดต่อกันหลายสัปดาห์",
    choices: [
      { label: "หากินทั้งวันทั้งคืน", outcomes: [{ chance: 1, text: "ฤดูร้อนสั้นนัก คุณต้องกินให้มากที่สุดก่อนหนาวกลับมา", effects: { fullness: 35, health: -5 } }] },
      { label: "พักผ่อนบ้าง", outcomes: [{ chance: 1, text: "คุณได้พักและยังหาอาหารได้พอ", effects: { fullness: 15, health: 10 } }] },
    ],
  },
  {
    id: "polar_blizzard",
    archetypes: LAND,
    stageTags: ["dependent", ...ACTIVE],
    requires: { biome: ["tundra", "rock_ice"] },
    weight: 3,
    text: "พายุหิมะพัดกระหน่ำ มองไม่เห็นอะไรเลยแม้แต่ระยะใกล้",
    choices: [
      {
        label: "ขดตัวหลบลม",
        outcomes: [
          { chance: 0.75, text: "หิมะทับถมรอบตัวกลายเป็นที่กำบังลม คุณรอดมาได้", effects: { fullness: -10 } },
          { chance: 0.25, text: "ความหนาวเย็นจัดเกินกว่าร่างกายจะทนไหว", effects: { health: -35, cause: "disaster" } },
        ],
      },
      {
        label: "เดินหาที่หลบที่ดีกว่า",
        outcomes: [
          { chance: 0.5, text: "คุณเจอซอกหินที่ลมพัดไม่ถึง", effects: { safety: 10, move: "near" } },
          { chance: 0.5, text: "คุณหลงทางกลางพายุจนหมดแรง", effects: { health: -25, fullness: -15, move: "near", cause: "disaster" } },
        ],
      },
    ],
  },

  // ---------- ป่าละเมาะแบบเมดิเตอร์เรเนียน ----------
  {
    id: "mediterranean_summer",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["mediterranean"] },
    weight: 3,
    text: "ฤดูร้อนแห้งแล้งยาวนาน พุ่มไม้แห้งกรอบและมีกลิ่นหอมฉุนลอยมาตามลม",
    fact: "พื้นที่แบบเมดิเตอร์เรเนียนมีฝนตกในฤดูหนาวและแห้งแล้งในฤดูร้อน พืชหลายชนิดแตกยอดใหม่ได้เร็วหลังไฟป่า",
    choices: [
      { label: "ออกหากินตอนเช้ามืดและพลบค่ำ", outcomes: [{ chance: 1, text: "ช่วงที่อากาศเย็นคุณหาอาหารได้พอ", effects: { fullness: 20, safety: 5 } }] },
      {
        label: "ไปหาแหล่งน้ำในหุบเขา",
        outcomes: [
          { chance: 0.7, text: "ลำธารในหุบเขายังมีน้ำไหลอยู่", effects: { health: 10, move: "near" } },
          { chance: 0.3, text: "ลำธารแห้งหมดแล้ว คุณเดินมาเสียเปล่า", effects: { fullness: -15, move: "near" } },
        ],
      },
    ],
  },

  // ---------- ทะเลทราย ----------
  {
    id: "desert_heat",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["desert"], sessile: false },
    weight: 4,
    text: "แดดเที่ยงร้อนจัด พื้นทรายร้อนจนแทบเหยียบไม่ได้",
    fact: "ทะเลทรายหลายแห่งกลางวันร้อนเกิน 40 องศา แต่กลางคืนเย็นลงมาก สัตว์ทะเลทรายส่วนใหญ่จึงออกหากินตอนกลางคืน",
    choices: [
      { label: "มุดลงใต้ทรายหรือหลบในเงาหิน", outcomes: [{ chance: 1, text: "ใต้ทรายลึกลงไปไม่กี่สิบเซนติเมตรเย็นกว่าข้างบนมาก", effects: { safety: 10, fullness: -5 } }] },
      {
        label: "รีบหาอาหารให้เสร็จ",
        outcomes: [
          { chance: 0.5, text: "คุณได้อาหารมาก่อนร่างกายจะร้อนเกินไป", effects: { fullness: 25, health: -5 } },
          { chance: 0.5, text: "ความร้อนทำให้ร่างกายคุณขาดน้ำ", effects: { health: -25, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "desert_bloom",
    archetypes: LAND,
    stageTags: ACTIVE,
    requires: { biome: ["desert"] },
    once: true,
    weight: 3,
    text: "ฝนตกหนักครั้งแรกในรอบหลายเดือน ไม่กี่วันต่อมาทะเลทรายก็ผลิดอกบานสะพรั่ง",
    fact: "เมล็ดพืชในทะเลทรายรอใต้ดินได้นานหลายปี แล้วงอกพร้อมกันเมื่อฝนตกมากพอ",
    choices: [
      { label: "ออกไปกินให้เต็มที่", outcomes: [{ chance: 1, text: "ช่วงเวลาอุดมสมบูรณ์สั้นๆ นี้ทำให้คุณแข็งแรงขึ้นมาก", effects: { fullness: 40, health: 10 } }] },
    ],
  },
  {
    id: "sandstorm",
    archetypes: LAND,
    stageTags: ["dependent", ...ACTIVE],
    requires: { biome: ["desert"] },
    weight: 2,
    text: "กำแพงฝุ่นสีส้มสูงเท่าภูเขาเคลื่อนเข้ามาหา",
    choices: [
      { label: "หลบหลังก้อนหิน", outcomes: [{ chance: 1, text: "ทรายกระหน่ำอยู่หลายชั่วโมง แต่คุณปลอดภัย", effects: { fullness: -10 } }] },
      {
        label: "หนีพายุ",
        outcomes: [
          { chance: 0.5, text: "คุณหนีพ้นขอบพายุไปได้", effects: { fullness: -15, move: "near" } },
          { chance: 0.5, text: "ทรายเข้าตาและจมูกจนหายใจลำบาก", effects: { health: -20, move: "near", cause: "disaster" } },
        ],
      },
    ],
  },

  // ---------- ป่าชายเลน ----------
  {
    id: "mangrove_low_tide",
    stageTags: ACTIVE,
    requires: { biome: ["mangrove"], sessile: false },
    weight: 4,
    text: "น้ำลงเผยหาดเลนและรากโกงกาง ปู หอย และปลาตีนออกมาเต็มไปหมด",
    fact: "ป่าชายเลนเป็นแหล่งอนุบาลของลูกปลาและกุ้งปูจำนวนมาก และช่วยลดความแรงของคลื่นจากพายุ",
    choices: [
      {
        label: "ออกหาอาหารบนหาดเลน",
        outcomes: [
          { chance: 0.75, text: "หาดเลนอุดมไปด้วยอาหาร คุณกินจนอิ่ม", effects: { fullness: 35 } },
          { chance: 0.25, text: "น้ำขึ้นเร็วกว่าที่คิด คุณต้องรีบหนีกลับ", effects: { fullness: 10, health: -10, cause: "accident" } },
        ],
      },
      { label: "หลบอยู่ในรากโกงกาง", outcomes: [{ chance: 1, text: "รากไม้พันกันแน่นหนาเป็นที่ซ่อนชั้นดี", effects: { safety: 15, fullness: 5 } }] },
    ],
  },
  {
    id: "mangrove_cleared",
    stageTags: ACTIVE,
    requires: { biome: ["mangrove"], sessile: false },
    weight: 1.5,
    threatenedWeight: 2,
    text: "เสียงเลื่อยยนต์ดังมาจากชายป่า ป่าชายเลนผืนหนึ่งกำลังถูกตัดเพื่อขุดบ่อเลี้ยงกุ้ง",
    fact: "ป่าชายเลนหลายแห่งในเอเชียตะวันออกเฉียงใต้ถูกเปลี่ยนเป็นบ่อเลี้ยงกุ้ง แต่หลายพื้นที่ในไทยกำลังปลูกป่าชายเลนคืน",
    choices: [
      { label: "ย้ายไปอยู่ป่าผืนที่ลึกเข้าไป", outcomes: [{ chance: 1, text: "ป่าผืนใหม่แน่นขึ้นเพราะทุกตัวย้ายมาที่นี่", effects: { safety: -5, fullness: -10, move: "near" } }] },
      {
        label: "อยู่ที่เดิม",
        outcomes: [
          { chance: 0.5, text: "พวกเขาตัดไม่ถึงตรงที่คุณอยู่", effects: { safety: -10 } },
          { chance: 0.5, text: "ที่อยู่ของคุณหายไปต่อหน้าต่อตา", effects: { health: -20, move: "near", cause: "human" } },
        ],
      },
    ],
  },

  // ---------- ทะเล ----------
  {
    id: "coastal_river_plume",
    archetypes: SEA,
    stageTags: ACTIVE,
    requires: { biome: ["coastal_sea"] },
    weight: 3,
    text: "หลังฝนตกหนัก น้ำขุ่นจากปากแม่น้ำพาตะกอนและสารอาหารแผ่ออกมาในทะเล",
    fact: "ทะเลชายฝั่งบนไหล่ทวีปมีพื้นที่ไม่ถึงหนึ่งในสิบของมหาสมุทร แต่เป็นแหล่งจับสัตว์น้ำส่วนใหญ่ของโลก",
    choices: [
      { label: "หากินในน้ำขุ่นที่อุดมสมบูรณ์", outcomes: [{ chance: 1, text: "แพลงก์ตอนและสัตว์เล็กเพิ่มขึ้นมาก อาหารมีเหลือเฟือ", effects: { fullness: 30 } }] },
      {
        label: "อยู่ในน้ำใสห่างออกไป",
        outcomes: [
          { chance: 0.7, text: "น้ำใสมองเห็นนักล่าได้ไกล คุณปลอดภัย", effects: { safety: 10, fullness: 5 } },
          { chance: 0.3, text: "ในน้ำใส นักล่าก็มองเห็นคุณเช่นกัน", effects: { safety: -15 } },
        ],
      },
    ],
  },
  {
    id: "open_ocean_drift",
    archetypes: SEA,
    stageTags: ACTIVE,
    requires: { biome: ["open_ocean"], sessile: false },
    weight: 4,
    text: "กลางทะเลเปิดกว้างใหญ่ ใต้ตัวคุณลึกลงไปหลายพันเมตร อาหารกระจายอยู่ห่างๆ กัน",
    fact: "ปลาทะเลเปิดหลายชนิดชอบรวมฝูงอยู่ใต้วัตถุลอยน้ำ ชาวประมงจึงทำซั้งลอยไว้ล่อปลา",
    choices: [
      { label: "ไปอยู่ใต้ขอนไม้ที่ลอยมา", outcomes: [{ chance: 1, text: "ใต้ขอนไม้มีปลาตัวเล็กมาหลบเป็นฝูง อาหารมาหาคุณเอง", effects: { fullness: 30, safety: -5 } }] },
      {
        label: "ออกเดินทางตามหาอาหาร",
        outcomes: [
          { chance: 0.5, text: "หลังเดินทางไกล คุณเจอฝูงอาหารใหญ่", effects: { fullness: 35, move: "far" } },
          { chance: 0.5, text: "ทะเลกว้างเกินไป คุณเดินทางนานแต่ได้อาหารน้อย", effects: { fullness: -15, move: "far" } },
        ],
      },
    ],
  },
];
